import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserPlus,
  Check,
  X,
  Video,
  Phone,
  Users as UsersIcon,
  Mail,
  Clock,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { CallModeDialog } from "@/components/CallModeDialog";
import { api } from "@/context/AuthContext";

/* Screen 10 — Friends. Small network of practice partners.

   Sections (top-down):
   - Add friend form (username) — now creates the friendship INSTANTLY
   - Incoming practice invites (accept → route to /video-call)
   - Incoming friend requests (legacy — only ever shows for pre-migration rows)
   - Friends list with live online dot + chat + invite actions

   Online status is refreshed as part of each poll of /api/friends — the
   backend annotates `is_online` based on presence-heartbeats from the
   signed-in sessions of each friend. */

const INVITE_POLL_MS = 5000;
const initialsOf = (name = "?") =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const AvatarChip = ({ picture, name, size = 40, isOnline }) => {
  const initial = (name || "?").charAt(0).toUpperCase();
  const cls = "shrink-0 rounded-full";
  const dotSize = Math.max(10, Math.round(size * 0.3));
  const dot =
    isOnline === undefined ? null : (
      <span
        aria-label={isOnline ? "Online" : "Offline"}
        title={isOnline ? "Online" : "Offline"}
        className={`absolute bottom-0 right-0 rounded-full ring-2 ring-[#141414] ${
          isOnline ? "bg-[#22C55E]" : "bg-zinc-600"
        }`}
        style={{ height: dotSize, width: dotSize }}
      />
    );
  const inner = picture ? (
    <img
      src={picture}
      alt={name}
      className={`${cls} object-cover`}
      style={{ height: size, width: size }}
    />
  ) : (
    <span
      className={`${cls} flex items-center justify-center bg-white/5 text-sm font-bold text-zinc-200`}
      style={{ height: size, width: size }}
    >
      {initial}
    </span>
  );
  if (dot === null) return inner;
  return (
    <span className="relative inline-flex" style={{ height: size, width: size }}>
      {inner}
      {dot}
    </span>
  );
};

export default function Friends() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    friends: [],
    pending_incoming: [],
    pending_outgoing: [],
  });
  const [invites, setInvites] = useState({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  const [handle, setHandle] = useState("");
  const [sending, setSending] = useState(false);
  // Friend the mode-picker dialog is currently targeting (null = closed).
  const [inviteTarget, setInviteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, i] = await Promise.all([
        api.get("/friends"),
        api.get("/friends/invites"),
      ]);
      setState(f.data);
      setInvites(i.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, INVITE_POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  const sendRequest = async (e) => {
    e?.preventDefault?.();
    const raw = handle.trim().toLowerCase().replace(/^@/, "");
    if (!raw) return;
    setSending(true);
    try {
      await api.post("/friends/request", { username: raw });
      toast.success(`You and ${raw} are now friends.`);
      setHandle("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add friend.");
    } finally {
      setSending(false);
    }
  };

  // Sort friends so online reps always appear first — this is the main thing
  // users come to this page to find.
  const sortedFriends = useMemo(() => {
    const copy = [...state.friends];
    copy.sort((a, b) => {
      if (!!a.is_online !== !!b.is_online) return a.is_online ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    return copy;
  }, [state.friends]);
  const onlineCount = sortedFriends.filter((f) => f.is_online).length;

  const respond = async (id, action) => {
    try {
      await api.post(`/friends/${id}/${action}`);
      await load();
    } catch {
      toast.error("Could not update the request.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this friend?")) return;
    try {
      await api.delete(`/friends/${id}`);
      await load();
    } catch {
      toast.error("Could not remove friend.");
    }
  };

  const invite = async (friend, callMode) => {
    try {
      const res = await api.post(`/friends/${friend.user_id}/invite`, {
        call_mode: callMode,
      });
      toast.success(
        `${callMode === "phone" ? "Phone" : "Video"} invite sent to ${
          friend.username ? friend.username : friend.name
        }.`
      );
      // Sender goes through the same pre-call device check as matchmaking
      // before entering the room; the friend joins after accepting. The
      // chosen mode rides along so both sides get the same call experience.
      navigate("/pre-call", {
        replace: false,
        state: {
          mode: "friend",
          room: res.data.room_name,
          cameraOn: callMode !== "phone",
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite.");
    }
  };

  const acceptInvite = async (inv) => {
    try {
      const res = await api.post(`/friends/invites/${inv.invite_id}/accept`);
      navigate("/pre-call", {
        replace: false,
        state: {
          mode: "friend",
          room: res.data.room_name,
          cameraOn: res.data.call_mode !== "phone",
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite.");
      await load();
    }
  };

  const declineInvite = async (inv) => {
    try {
      await api.post(`/friends/invites/${inv.invite_id}/decline`);
      await load();
    } catch {
      toast.error("Could not decline invite.");
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="friends" />

      <main className="flex-1 px-6 py-7 md:px-12 md:py-10" data-testid="friends-page">
        <div className="mb-8 flex items-center justify-between md:hidden">
          <Logo className="text-2xl" />
        </div>

        <div className="mx-auto max-w-3xl">
          <header>
            <h1 className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">
              Friends
            </h1>
            <p className="mt-2 text-sm text-zinc-400">
              Your personal roster of practice partners. Invite them straight into a
              private mock call — no matchmaking needed.
            </p>
          </header>

          {/* Add friend by username */}
          <form
            onSubmit={sendRequest}
            className="mt-8 flex items-center gap-2 rounded-2xl border border-white/10 bg-[#141414] p-2"
          >
            <span className="pl-3 text-sm text-zinc-500">@</span>
            <input
              data-testid="friend-username-input"
              value={handle}
              onChange={(e) => setHandle(e.target.value.toLowerCase())}
              placeholder="username"
              maxLength={20}
              className="flex-1 bg-transparent px-1 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none"
            />
            <button
              type="submit"
              data-testid="send-friend-request-btn"
              disabled={sending || !handle.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E52525] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#c81f1f] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <UserPlus size={15} />
              {sending ? "Adding…" : "Add friend"}
            </button>
          </form>

          {/* Incoming practice invites — highest priority so they're first. */}
          {invites.incoming.length > 0 && (
            <section className="mt-8" data-testid="incoming-invites">
              <h2 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-400">
                <Video size={13} className="text-[#E52525]" /> Practice invites
              </h2>
              <div className="space-y-2">
                {invites.incoming.map((inv) => (
                  <motion.div
                    key={inv.invite_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 rounded-2xl border border-[#E52525]/40 bg-[#E52525]/5 p-4"
                  >
                    <AvatarChip picture={inv.from_user?.picture} name={inv.from_user?.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">
                        {inv.from_user?.name || inv.from_user?.username || "A friend"}
                      </p>
                      <p
                        className="flex items-center gap-1 text-xs text-zinc-400"
                        data-testid={`invite-mode-${inv.invite_id}`}
                      >
                        {inv.call_mode === "phone" ? (
                          <>
                            <Phone size={11} className="text-[#FF5A5A]" /> wants a
                            phone mock (camera off)
                          </>
                        ) : (
                          <>
                            <Video size={11} className="text-[#FF5A5A]" /> wants a
                            video mock call
                          </>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => declineInvite(inv)}
                      data-testid={`invite-decline-${inv.invite_id}`}
                      className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-zinc-300 hover:border-white/25 hover:text-white"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => acceptInvite(inv)}
                      data-testid={`invite-accept-${inv.invite_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#E52525] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#c81f1f]"
                    >
                      <Video size={13} /> Join
                    </button>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* Incoming friend requests */}
          {state.pending_incoming.length > 0 && (
            <section className="mt-8" data-testid="incoming-requests">
              <h2 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-400">
                <Mail size={13} /> Friend requests
              </h2>
              <div className="space-y-2">
                {state.pending_incoming.map((r) => (
                  <motion.div
                    key={r.friendship_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#141414] p-4"
                  >
                    <AvatarChip picture={r.picture} name={r.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{r.name}</p>
                      {r.username && (
                        <p className="text-xs text-zinc-400">{r.username}</p>
                      )}
                    </div>
                    <button
                      onClick={() => respond(r.friendship_id, "decline")}
                      data-testid={`friend-decline-${r.friendship_id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 hover:bg-white/5 hover:text-white"
                      title="Decline"
                    >
                      <X size={15} />
                    </button>
                    <button
                      onClick={() => respond(r.friendship_id, "accept")}
                      data-testid={`friend-accept-${r.friendship_id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-[#22C55E] text-white hover:bg-[#1ea24a]"
                      title="Accept"
                    >
                      <Check size={15} />
                    </button>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* Friends list */}
          <section className="mt-8" data-testid="friends-list">
            <h2 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-400">
              <UsersIcon size={13} /> Your friends
              {!loading && sortedFriends.length > 0 && (
                <span
                  data-testid="friends-online-count"
                  className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-[#22C55E]/25 bg-[#22C55E]/10 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-[#22C55E]"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
                  {onlineCount} online
                </span>
              )}
            </h2>
            {loading ? (
              <div className="rounded-2xl border border-white/10 bg-[#141414] py-14 text-center text-sm text-zinc-500">
                Loading…
              </div>
            ) : sortedFriends.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#141414] py-10 text-center">
                <p className="text-sm text-zinc-400">
                  No friends yet. Add someone above or after a mock call.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedFriends.map((f) => (
                  <div
                    key={f.friendship_id}
                    data-testid={`friend-${f.friendship_id}`}
                    data-online={f.is_online ? "true" : "false"}
                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#141414] p-4"
                  >
                    <AvatarChip
                      picture={f.picture}
                      name={f.name}
                      isOnline={!!f.is_online}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{f.name}</p>
                      <p className="flex items-center gap-1.5 text-xs text-zinc-400">
                        {f.username && <span>{f.username}</span>}
                        {f.username && (
                          <span aria-hidden="true" className="text-zinc-700">·</span>
                        )}
                        <span
                          className={
                            f.is_online ? "text-[#22C55E]" : "text-zinc-500"
                          }
                        >
                          {f.is_online ? "Online now" : "Offline"}
                        </span>
                      </p>
                    </div>
                    <button
                      onClick={() => remove(f.friendship_id)}
                      data-testid={`friend-remove-${f.friendship_id}`}
                      className="rounded-full px-3 py-1.5 text-xs font-medium text-zinc-500 hover:text-[#E52525]"
                    >
                      Remove
                    </button>
                    <button
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("salespit:open-chat-with", {
                            detail: { friend: f },
                          })
                        )
                      }
                      data-testid={`friend-chat-${f.friendship_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-transparent px-3.5 py-1.5 text-xs font-semibold text-zinc-200 hover:border-white/25 hover:text-white"
                    >
                      <MessageSquare size={13} /> Chat
                    </button>
                    <button
                      onClick={() => setInviteTarget(f)}
                      data-testid={`friend-invite-${f.friendship_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#E52525] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#c81f1f]"
                    >
                      <Video size={13} /> Invite
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Outgoing pending (subtle) */}
          {state.pending_outgoing.length > 0 && (
            <section className="mt-6" data-testid="outgoing-requests">
              <h3 className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-zinc-600">
                <Clock size={11} /> Pending sent
              </h3>
              <div className="flex flex-wrap gap-2">
                {state.pending_outgoing.map((r) => (
                  <span
                    key={r.friendship_id}
                    className="rounded-full border border-white/5 bg-[#141414] px-3 py-1 text-xs text-zinc-500"
                  >
                    {r.username ? r.username : r.name}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Call-mode picker for the Invite button — Camera On or Camera Off,
          then the existing invite flow runs with that mode. */}
      <CallModeDialog
        open={!!inviteTarget}
        onOpenChange={(open) => {
          if (!open) setInviteTarget(null);
        }}
        friendName={
          inviteTarget
            ? inviteTarget.username
              ? inviteTarget.username
              : inviteTarget.name
            : undefined
        }
        onSelect={(mode) => {
          const target = inviteTarget;
          setInviteTarget(null);
          if (target) invite(target, mode);
        }}
      />
    </div>
  );
}
